import _debounce from 'lodash/debounce';
import type {
  AssignFavoriteGroupsResponse,
  CreateFavoriteGroupResponse,
  FavoriteGroupRef,
  FavoriteGroupValidationErrorResponse,
} from '@/types/favorites.type';

type ResolvedFavoriteOption = {
  fn: {
    switch?: (elem: HTMLElement) => void;
    formAction?: (
      elem: HTMLElement,
      option: ResolvedFavoriteOption
    ) => Promise<void>;
  };
  button: {
    toggleDisabled: boolean;
    toggleMethod: false | [string, string];
  };
};

type FavoriteOption = {
  fn?: ResolvedFavoriteOption['fn'];
  button?: Partial<ResolvedFavoriteOption['button']>;
};

const DEFAULT_OPTION: ResolvedFavoriteOption = {
  fn: {},
  button: {
    toggleDisabled: true,
    toggleMethod: false,
  },
};

function getCsrfToken(): string {
  const token = document.querySelector<HTMLMetaElement>(
    'meta[name=csrf-token]'
  )?.content;
  if (!token) {
    throw new Error('CSRF token not found');
  }
  return token;
}

class Favorite {
  private readonly classname: string;
  private readonly rootElement: Document | Element;
  private readonly option: ResolvedFavoriteOption;
  private readonly switchHandler: (elem: HTMLElement) => void;
  private readonly formActionHandler: (
    elem: HTMLElement,
    option: ResolvedFavoriteOption
  ) => Promise<void>;
  private readonly buttons: NodeListOf<HTMLElement>;

  constructor(
    classname: string,
    option: FavoriteOption = {},
    rootElement: Document | Element = document
  ) {
    if (!classname) throw new Error('classname is required');
    if (!rootElement) throw new Error('rootElement is required');

    this.classname = classname;
    this.rootElement = rootElement;
    this.option = {
      fn: { ...DEFAULT_OPTION.fn, ...option.fn },
      button: { ...DEFAULT_OPTION.button, ...option.button },
    };

    this.switchHandler = this.option.fn.switch ?? this.defaultSwitch.bind(this);
    this.formActionHandler =
      this.option.fn.formAction ?? this.defaultFormAction.bind(this);

    this.buttons =
      this.rootElement.querySelectorAll<HTMLElement>(classname);
    if (this.buttons.length < 1) return;

    this.buttons.forEach((elem) => this.switchHandler(elem));
  }

  private defaultSwitch(elem: HTMLElement): void {
    const { toggleDisabled } = this.option.button;

    elem.addEventListener(
      'click',
      _debounce(() => {
        if (toggleDisabled && isToggleable(elem)) {
          elem.disabled = !elem.disabled;
        }
        void this.formActionHandler(elem, this.option).catch(console.error);
      }, 200)
    );
  }

  private async defaultFormAction(
    elem: HTMLElement,
    option: ResolvedFavoriteOption
  ): Promise<void> {
    const { dataset } = elem;
    if (!dataset.url || !dataset.method) {
      throw new Error('data-url and data-method are required');
    }
    const isElemToggleable = isToggleable(elem);
    const previousDisabled = isElemToggleable ? elem.disabled : false;
    const { toggleDisabled, toggleMethod } = option.button;

    try {
      const params = new URLSearchParams({
        favoriteable_type: dataset.key ?? '',
        favoriteable_id: dataset.value ?? '',
      });
      const response = await fetch(`${dataset.url}?${params.toString()}`, {
        method: dataset.method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`response error: ${response.status}`);
      }

      if (Array.isArray(toggleMethod)) {
        elem.dataset.method =
          dataset.method === toggleMethod[0] ? toggleMethod[1] : toggleMethod[0];
      }
    } finally {
      if (toggleDisabled && isElemToggleable) {
        elem.disabled = !previousDisabled;
      }
    }
  }

  // 複雑すぎるので外に出す
  indexManipulation(): void {
    const body = this.rootElement.querySelector('.js-favorite-post-body');
    if (!body) return;

    const content = document.createElement('div');
    const h4 = document.createElement('h4');

    content.classList.add('mypage-Article_Content');
    h4.classList.add('mypage-Article_Label');
    h4.textContent = '投稿がありません';

    content.appendChild(h4);
    body.innerHTML = '';
    body.appendChild(content);
  }
}

class FavoriteModal {
  toggle(modal: HTMLElement, _button?: HTMLElement): void {
    modal.hasAttribute('open')
      ? modal.removeAttribute('open')
      : modal.setAttribute('open', '');
  }

  constructor(
    classname: string,
    fn?: (modal: HTMLElement, button: HTMLElement) => void
  ) {
    if (!classname) throw new Error('classname is required');

    const buttons = document.querySelectorAll<HTMLElement>(classname);
    if (buttons.length < 1) return;

    if (typeof fn === 'function') {
      this.toggle = fn;
    }

    buttons.forEach((button) => {
      if (button.dataset.targetModal === undefined) {
        throw new Error('data-target-modal is required');
      }

      const modal = document.querySelector<HTMLElement>(
        `[data-modal-name="${button.dataset.targetModal}"]`
      );
      if (!modal) return;

      button.addEventListener('click', (event) => {
        event.stopImmediatePropagation();
        this.toggle(modal, button);
      });
    });
  }
}

class FavoriteRemoveModal extends FavoriteModal {
  override toggle(modal: HTMLElement, favoriteButton?: HTMLElement): void {
    const { actionButton } = modal.dataset;
    const url = favoriteButton?.dataset.url;
    if (actionButton === undefined || url === undefined) {
      throw new Error('data-action-button and data-url required');
    }

    const removeButton = modal.querySelector<HTMLAnchorElement>(
      `[data-button-name="${actionButton}"]`
    );
    if (removeButton === null) {
      throw new Error('removeButton is not found');
    }

    removeButton.href = url;
    modal.setAttribute('open', '');
  }
}

class FavoriteGroupingModal extends FavoriteModal {
  override toggle(modal: HTMLElement, favoriteButton?: HTMLElement): void {
    // モーダル側のアクションボタン
    const { actionButton } = modal.dataset;
    if (actionButton === undefined) {
      throw new Error('data-action-button is required');
    }
    const dataset = favoriteButton?.dataset;
    const favoriteableType = dataset?.favoriteableType;
    const favoriteableId = dataset?.favoriteableId;
    const groups = dataset?.groups;
    if (favoriteableType === undefined || favoriteableId === undefined) {
      throw new Error(
        'data-favoriteable-type and data-favoriteable-id are required'
      );
    }

    const checkboxes = modal.querySelectorAll<HTMLInputElement>(
      '[data-input-name="grouping-input"]'
    );
    const urlNames = (JSON.parse(groups || '[]') as FavoriteGroupRef[]).map(
      (group) => group.url_name
    );
    const groupIds: string[] = [];

    checkboxes.forEach((checkbox) => {
      if (urlNames.includes(checkbox.value)) {
        checkbox.checked = true;
        groupIds.push(checkbox.value);
      } else {
        checkbox.checked = false;
      }
    });

    const groupingButton = modal.querySelector<HTMLElement>(
      `[data-button-name="${actionButton}"]`
    );
    if (groupingButton === null) {
      throw new Error('groupingButton is not found');
    }

    groupingButton.dataset.groupIds = JSON.stringify(groupIds);
    groupingButton.dataset.favoriteableType = favoriteableType;
    groupingButton.dataset.favoriteableId = favoriteableId;
    modal.setAttribute('open', '');
  }
}

class FavoriteAssignGroups {
  constructor(classname: string) {
    if (!classname) return;

    const button = document.querySelector<HTMLButtonElement>(classname);
    if (!button) return;

    button.addEventListener(
      'click',
      _debounce(() => {
        if (!button.dataset.method) return;
        button.disabled = !button.disabled;
        void this.formAction(button).catch(console.error);
      }, 200)
    );
  }

  async formAction(elem: HTMLButtonElement): Promise<void> {
    const { dataset } = elem;
    const previousDisabled = elem.disabled;
    if (!dataset.method) return;

    try {
      const params = new URLSearchParams();
      const groupIds = JSON.parse(dataset.groupIds || '[]') as string[];
      params.append('favoriteable_type', dataset.favoriteableType ?? '');
      params.append('favoriteable_id', dataset.favoriteableId ?? '');
      groupIds.forEach((id) => {
        params.append('favorite_group_ids[]', id);
      });

      const response = await fetch(`${dataset.url}?${params.toString()}`, {
        method: dataset.method.toUpperCase(),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`response error: ${response.status}`);
      }

      const { favorite_groups } =
        (await response.json()) as AssignFavoriteGroupsResponse;

      const favorite = document.querySelector<HTMLElement>(
        `[data-target-modal="grouping-modal"][data-favoriteable-type="${dataset.favoriteableType}"][data-favoriteable-id="${dataset.favoriteableId}"]`
      );
      if (!favorite) return;
      favorite.dataset.groups = JSON.stringify(favorite_groups);

      const modal = document.querySelector<HTMLElement>(
        '[data-modal-name="grouping-modal"]'
      );
      modal?.removeAttribute('open');

      if (favorite.dataset.closest !== undefined) {
        const grouplist = favorite.closest('.mypage-FavoriteGroup');
        if (!grouplist) return;
        grouplist
          .querySelectorAll('.mypage-FavoriteGroup_Item:not(.js-action_btn)')
          .forEach((item) => item.remove());
        favorite_groups.forEach((group) => {
          const item = document.createElement('li');
          const label = document.createElement('span');
          item.classList.add('mypage-FavoriteGroup_Item');
          label.classList.add('mypage-FavoriteGroup_Button');
          label.textContent = group.name;
          item.appendChild(label);
          grouplist.prepend(item);
        });
      }
    } finally {
      elem.disabled = !previousDisabled;
    }
  }
}

class FavoriteMyPage {
  constructor(classname: string) {
    if (classname === undefined) {
      throw new Error('classname is required');
    }

    new Favorite(classname, {
      fn: {
        formAction: this.removeFavoriteAction.bind(this),
      },
    });
  }

  async removeFavoriteAction(
    elem: HTMLElement,
    option: ResolvedFavoriteOption
  ): Promise<void> {
    const { dataset } = elem;
    if (!dataset.url || !dataset.method || !dataset.key || !dataset.value) {
      throw new Error(
        'data-url, data-method, data-key, and data-value are required'
      );
    }
    const isElemToggleable = isToggleable(elem);
    const previousDisabled = isElemToggleable ? elem.disabled : false;
    const { toggleDisabled } = option.button;

    try {
      const response = await fetch(
        `${dataset.url}?${dataset.key}_id=${dataset.value}`,
        {
          method: dataset.method,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`response error: ${response.status}`);
      }

      if (dataset.targetModal === undefined) {
        throw new Error('data-target-modal is required');
      }

      const modal = document.querySelector<HTMLElement>(
        `[data-modal-name="${dataset.targetModal}"]`
      );
      if (modal === null) {
        throw new Error('modal is not found');
      }

      modal.removeAttribute('open');

      this.removeFavoriteDOM(elem);
    } finally {
      if (toggleDisabled && isElemToggleable) {
        elem.disabled = !previousDisabled;
      }
    }
  }

  removeFavoriteDOM(relative: HTMLElement): void {
    const { dataset } = relative;
    if (dataset.key === undefined || dataset.value === undefined) {
      throw new Error('data-key or data-value is required');
    }

    const target = document.querySelector<HTMLElement>(
      [
        dataset.removeClass ?? '',
        `[data-value="${dataset.value}"]`,
        `[data-key="${dataset.key}"]`,
      ].join('')
    );

    dataset.value = '';
    dataset.key = '';

    if (target === null) {
      throw new Error('target is not found');
    }

    const articleSelector = target.dataset.article;
    if (!articleSelector) {
      throw new Error('target.dataset.article is required');
    }
    const articles = document.querySelectorAll(articleSelector);
    if (articles.length < 1) {
      throw new Error('articles is not found');
    }

    setTimeout(() => {
      articles.forEach((article) => article.remove());
      const panels = document.querySelectorAll<HTMLElement>(
        '[data-favorite-panel]'
      );
      if (panels.length < 1) {
        throw new Error('panels is not found');
      }

      panels.forEach((panel) => {
        if (panel.children.length >= 1) return;

        const article = document.createElement('article');
        const content = document.createElement('div');
        const h4 = document.createElement('h4');

        article.classList.add('mypage-Article');
        content.classList.add('mypage-Article_Content');
        content.classList.add('-favorite');
        h4.classList.add('mypage-Article_Isnt');
        h4.textContent = 'お気に入りがありません';
        content.appendChild(h4);
        article.appendChild(content);

        panel.nextElementSibling?.remove();
        panel.classList.remove(...panel.classList);
        panel.classList.add('mypage-Panel_Body');
        panel.appendChild(article);
      });
    }, 400);
  }
}

class FavoriteCreateGroup {
  private readonly showFormButton: HTMLElement | null;
  private readonly createGroupForm: HTMLElement | null;

  constructor() {
    const submitButton = document.querySelector<HTMLButtonElement>(
      '.js-create-group'
    );
    this.showFormButton = document.querySelector<HTMLElement>(
      '.js-show_group_name_form'
    );
    this.createGroupForm = document.querySelector<HTMLElement>(
      '.js-create_group_form'
    );

    if (!submitButton) return;
    submitButton.addEventListener(
      'click',
      _debounce(() => {
        if (!submitButton.dataset.method) return;
        submitButton.disabled = !submitButton.disabled;
        void this.formAction(submitButton).catch(console.error);
      }, 200)
    );

    this.showFormButton?.addEventListener('click', () => {
      this.showFormButton?.classList.add('hidden');
      this.createGroupForm?.classList.remove('hidden');
    });
  }

  async formAction(elem: HTMLButtonElement): Promise<void> {
    const { dataset } = elem;
    const previousDisabled = elem.disabled;
    if (!dataset.method) return;

    try {
      const params = new URLSearchParams();
      const createGroupNameInput = document.getElementById(
        'js-create_group_name_input'
      ) as HTMLInputElement | null;
      if (!createGroupNameInput) return;
      params.append('favorite_group[name]', createGroupNameInput.value);

      const response = await fetch(`${dataset.url}?${params.toString()}`, {
        method: dataset.method.toUpperCase(),
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const validationErrors = document.querySelector<HTMLElement>(
        '.js-validation_errors'
      );
      if (validationErrors) validationErrors.innerHTML = '';

      // バリデーションエラー
      if (response.status === 400) {
        const { validation_errors_html } =
          (await response.json()) as FavoriteGroupValidationErrorResponse;
        if (validationErrors)
          validationErrors.innerHTML = validation_errors_html;
        return;
      }

      if (!response.ok) {
        throw new Error(`response error: ${response.status}`);
      }

      const { favorite_group_list_html, is_max_group_reached } =
        (await response.json()) as CreateFavoriteGroupResponse;
      createGroupNameInput.value = '';

      // 現在のチェック状態を取得
      const currentCheckedItems = document.querySelectorAll<HTMLInputElement>(
        '[data-input-name="grouping-input"]:checked'
      );
      // チェックボックスの再作成
      const favoriteGroupsList = document.querySelector<HTMLElement>(
        '.js-favorite_group_list'
      );
      if (favoriteGroupsList)
        favoriteGroupsList.innerHTML = favorite_group_list_html;
      // チェック状態を再設定
      currentCheckedItems.forEach((element) => {
        const restored = document.getElementById(element.id);
        if (restored instanceof HTMLInputElement) restored.checked = true;
      });
      // イベントの再設定
      new FavoriteGroupCheckButtonState(
        '.js-select-group[data-checked-items=".peer"]'
      );

      if (!is_max_group_reached) {
        this.showFormButton?.classList.remove('hidden');
      }

      document
        .querySelector<HTMLElement>('.js-created_group_list')
        ?.classList.remove('hidden');

      document
        .querySelector<HTMLElement>('.js-no-group-annotation')
        ?.classList.add('hidden');

      const selectGroupButton = document.querySelector<HTMLButtonElement>(
        '.js-select-group'
      );
      if (selectGroupButton) {
        selectGroupButton.classList.remove(
          'u-bg-gray-2',
          'u-text-gray-6',
          'u-border-gray-2'
        );
        selectGroupButton.classList.add(
          'bg-[--main-color]',
          'text-[--button-text-color]'
        );
        selectGroupButton.disabled = false;
      }

      this.createGroupForm?.classList.add('hidden');
    } finally {
      elem.disabled = !previousDisabled;
    }
  }
}

class FavoriteGroupCheckButtonState {
  private readonly button: HTMLElement | null;
  private readonly checkedItems: NodeListOf<HTMLInputElement> | null;

  constructor(classname: string) {
    this.button = null;
    this.checkedItems = null;
    if (!classname) return;

    const button = document.querySelector<HTMLElement>(classname);
    if (!button || !button.dataset.checkedItems) return;
    this.button = button;

    this.checkedItems = document.querySelectorAll<HTMLInputElement>(
      button.dataset.checkedItems
    );
    if (this.checkedItems.length === 0) return;

    this.changeButtonState();

    this.checkedItems.forEach((item) => {
      item.addEventListener('change', () => {
        this.changeButtonState();
      });
    });
  }

  changeButtonState(): void {
    if (!this.button || !this.checkedItems) return;
    const ids = Array.from(this.checkedItems)
      .filter((item) => item.checked)
      .map((item) => item.value);
    this.button.dataset.groupIds = JSON.stringify(ids);
  }
}

function isToggleable(
  elem: HTMLElement
): elem is HTMLButtonElement | HTMLInputElement {
  return elem instanceof HTMLButtonElement || elem instanceof HTMLInputElement;
}

export {
  Favorite,
  FavoriteMyPage,
  FavoriteModal,
  FavoriteRemoveModal,
  FavoriteGroupingModal,
  FavoriteAssignGroups,
  FavoriteCreateGroup,
  FavoriteGroupCheckButtonState,
};
